import { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import Checkbox from 'components/inputs/Checkbox';
import AutoSizedTextArea from 'components/inputs/AutoSizedTextArea';
import Select from 'components/inputs/Select';
import { useActiveAssignment } from '../ActiveAssignmentProvider';
import DropdownMenu from 'components/DropdownMenu';
import Button from 'components/inputs/Button';
import TransparentSpinner from 'components/spinners/TransparentSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  textarea {
    width: 100%;
    max-height: 250px;
    min-width: 100%;
    max-width: 100%;
    min-height: 3em;
    resize: none;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: flex-start;
  width: 100%;
`;

const Left = styled.div`
  display: inline-flex;
  margin-left: 0;
  gap: 6px;
  align-items: center;

  span {
    margin-bottom: 3px;
    margin-right: 6px;
  }
`;

const Right = styled.div`
  margin-left: auto;
`;

interface CommentInputProps {
  onAddComment(
    comment: string,
    internalComment: boolean,
    activeAssignment?: number
  ): void;
  loading: boolean;
  showInternalCommentOption?: boolean;
}

const CommentInput: React.FC<CommentInputProps> = ({
  onAddComment,
  loading,
  showInternalCommentOption,
}) => {
  const theme = useTheme();
  const activeAssignmentContext = useActiveAssignment();

  const [commentInput, setCommentInput] = useState('');
  const [internal, setInternal] = useState(false);

  const handleCreateGenericCommentClick = async () => {
    if (!commentInput) return;
    onAddComment(commentInput, internal, undefined);
  };

  const handleCreateCommentClick = async () => {
    if (!commentInput) return;
    onAddComment(
      commentInput,
      internal,
      activeAssignmentContext?.activeAssignment
    );
  };

  return (
    <Wrapper>
      <AutoSizedTextArea
        disabled={loading}
        onChange={(e) => setCommentInput(e.currentTarget.value)}
        placeholder="Skriv en kommentar..."
        value={commentInput}
        onKeyDown={(eve) => {
          // if cmd + enter, trigger handleCreateCommentClick
          if (eve.ctrlKey && eve.key === 'Enter') {
            handleCreateGenericCommentClick();
          }
        }}
      />

      <Row>
        <Left>
          {showInternalCommentOption && (
            <Checkbox
              checked={internal}
              onChange={() => setInternal(!internal)}
            >
              Intern kommentar
            </Checkbox>
          )}
        </Left>
        <Right>
          <Button
            disabled={loading || !commentInput}
            onClick={handleCreateGenericCommentClick}
            type="button"
          >
            {loading ? (
              <TransparentSpinner
                color={theme.colors.foreground.button}
                small
              />
            ) : (
              'Skicka'
            )}
          </Button>
        </Right>
      </Row>
      {activeAssignmentContext && (
        <Row>
          <Right>
            <DropdownMenu
              label={<span>Skicka till uppdrag</span>}
              loading={loading}
              disabled={!commentInput}
              asButton
              content={
                <Wrapper style={{ padding: '10px' }}>
                  <Row>
                    <Left>
                      <Select
                        onChange={(e) => {
                          const selectedAssignmentId = Number(e.target.value);
                          activeAssignmentContext?.setActiveAssignment(
                            selectedAssignmentId
                          );
                        }}
                        value={activeAssignmentContext?.activeAssignment}
                      >
                        <option value="">Alla uppdrag i ärendet</option>
                        {activeAssignmentContext?.assignmentIds.map((id) => (
                          <option key={id} value={id}>
                            Uppdrag {id}
                          </option>
                        ))}
                      </Select>
                    </Left>
                    <Right>
                      <Button
                        disabled={loading || !commentInput}
                        onClick={handleCreateCommentClick}
                        type="button"
                        style={{
                          height: 30,
                          padding: '0 10px',
                        }}
                      >
                        {loading ? (
                          <TransparentSpinner
                            color={theme.colors.foreground.button}
                            small
                          />
                        ) : (
                          <FontAwesomeIcon icon={faPaperPlane} />
                        )}
                      </Button>
                    </Right>
                  </Row>
                </Wrapper>
              }
            />
          </Right>
        </Row>
      )}
    </Wrapper>
  );
};

export default CommentInput;
